import { HttpErrorResponse } from '@angular/common/http'

import { HARNESS_ERROR_CODES } from '@flowx/designer/modules/ai-builder/types/harness.types'
import { isRecord } from '@flowx/designer/modules/ai-builder/utils/payload.util'

const REFUSAL_MESSAGES = {
  amendment_pending:
    'A change to the plan is waiting for your review. Review the change before continuing.',
  restart_needs_review:
    'The source branch has new saved changes. Review the restart before trying again.',
  saved_changes_review: 'This branch has changes that are not committed. Review them to continue.',
} as const satisfies Readonly<Record<string, string>>

/** A 409 refusal the harness types, with a maker sentence of its own. */
export type HarnessRunRefusal = keyof typeof REFUSAL_MESSAGES

/** An untranslated message key such as `error.validation.maker_file.not_found`. */
const ERROR_KEY = /^error\.[\w.-]+$/

/** The sentence a service's problem body carries, where the transport error only has the status
 * line: its `message`, else its English `defaultMessage` when `message` is only a key; null when
 * there is none. */
export function problemMessage(error: unknown): string | null {
  const body = error instanceof HttpErrorResponse && isRecord(error.error) ? error.error : {}

  return (
    [body['message'], body['defaultMessage']].find(
      (text): text is string => typeof text === 'string' && !!text && !ERROR_KEY.test(text)
    ) ?? null
  )
}

/** A failed call as the maker should read it: in the service's own words when its body has some. */
export function inServiceWords(error: unknown): unknown {
  const said = problemMessage(error)

  return said ? new Error(said) : error
}

/** The harness's code for a failed call or stream. Without one the stream is taken as cut short:
 * that is the failure that arrives without a body to say otherwise. */
export function harnessErrorCode(error: unknown): string {
  return readErrorField(error, 'code', HARNESS_ERROR_CODES.streamTruncated)
}

/** The harness's own sentence for a failed call or stream. */
export function harnessErrorMessage(error: unknown): string {
  return readErrorField(error, 'message', 'The harness could not be reached.')
}

/** The typed 409 refusal a failed run call came back with, null for any other failure. */
export function harnessRunRefusal(error: unknown): HarnessRunRefusal | null {
  const reason = harnessErrorReason(error)

  return harnessErrorHttpStatus(error) === 409 && reason && Object.hasOwn(REFUSAL_MESSAGES, reason)
    ? (reason as HarnessRunRefusal)
    : null
}

/** Execution refusals in maker words where the harness has typed one, else in its own sentence. */
export function harnessRunErrorMessage(error: unknown): string {
  const refusal = harnessRunRefusal(error)

  return refusal ? REFUSAL_MESSAGES[refusal] : harnessErrorMessage(error)
}

/** A structured refusal's own code (`target_headers_required`, `context_moved`, …), when it had
 * one. */
export function harnessErrorReason(error: unknown): string | undefined {
  const reason = errorField(error, 'reason')

  return typeof reason === 'string' ? reason : undefined
}

/** The structured body a refusal carries beside its reason, when it carried one. */
export function harnessErrorDetail(error: unknown): Record<string, unknown> | undefined {
  const detail = errorField(error, 'detail')

  return detail && typeof detail === 'object' ? (detail as Record<string, unknown>) : undefined
}

/** The HTTP status a failed call came back with, when it reached the harness at all. */
export function harnessErrorHttpStatus(error: unknown): number | undefined {
  const status = errorField(error, 'status')

  return typeof status === 'number' ? status : undefined
}

function readErrorField(error: unknown, key: 'code' | 'message', fallback: string): string {
  const value = errorField(error, key)

  return typeof value === 'string' ? value : fallback
}

function errorField(error: unknown, key: string): unknown {
  return (error as Record<string, unknown> | null | undefined)?.[key]
}
